import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import arabic from "./ar.json";
import english from "./en.json";

const LanguageContext = createContext(null);
const storageKey = "portfolio-language";

function initialLanguage() {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved === "en" || saved === "ar") return saved;
  } catch (_) { /* Language switching also works when storage is unavailable. */ }
  return "en";
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(initialLanguage);
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    try { localStorage.setItem(storageKey, language); } catch (_) { /* Optional persistence. */ }
  }, [language]);

  const value = useMemo(() => {
    const resources = language === "ar" ? arabic : english;
    const t = (key) => resources[key] ?? english[key] ?? key;
    // Translate content only; URLs, images, icons, and technology identifiers stay intact.
    const fields = new Set(["date", "title", "subTitle", "jobTitle", "companyName", "description", "summary", "alt", "descriptionPoints", "descriptionList", "note", "more", "projectType", "keyFeatures", "linkPlaceHolder"]);
    const localize = (data, field) => {
      if (React.isValidElement(data)) return data;
      if (Array.isArray(data)) return data.map((item) => localize(item, field));
      if (data && typeof data === "object") return Object.fromEntries(
        Object.entries(data).map(([key, item]) => [key, localize(item, key)])
      );
      return fields.has(field) ? t(data) : data;
    };
    return { language, setLanguage, t, localize };
  }, [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
